import { PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from "react";

/** Leftward drag (px) past which releasing commits the "Done" action. */
const COMMIT_THRESHOLD = 96;
/** How far the card can be dragged — a little past the commit point for feedback. */
const MAX_DRAG = 120;
/** Movement (px) beyond which a pointer gesture counts as a swipe rather than a tap/click. */
const MOVE_THRESHOLD = 8;

interface UseSwipeToDoneOptions {
	/** Invoked once when a leftward swipe is released past {@link COMMIT_THRESHOLD}. */
	onCommit: () => void;
	/** When true the gesture is inert (e.g. collapsed card, no done handler). */
	disabled?: boolean;
}

interface UseSwipeToDoneResult {
	/** Current horizontal offset of the card (≤ 0). Feed into a `translateX`. */
	dragX: number;
	/** True while an active leftward drag is in progress (disables the transition). */
	isDragging: boolean;
	/** Pointer handlers to spread on the swipeable element. */
	bind: {
		onPointerDown: (e: ReactPointerEvent) => void;
		onPointerMove: (e: ReactPointerEvent) => void;
		onPointerUp: (e: ReactPointerEvent) => void;
		onPointerCancel: (e: ReactPointerEvent) => void;
	};
	/**
	 * Whether the click that fires immediately after a swipe should be ignored. The card's `onClick`
	 * (select lead) must early-return on this so a swipe never doubles as a select.
	 */
	shouldIgnoreClick: () => boolean;
}

/**
 * Swipe-a-card-left-to-mark-done gesture, driven by pointer events (mouse + touch). Tracks a
 * leftward drag, reveals the offset via {@link UseSwipeToDoneResult.dragX}, and calls `onCommit`
 * when released past the threshold. Movement below {@link MOVE_THRESHOLD} is treated as a click, so
 * tapping the card still selects the lead.
 */
export const useSwipeToDone = ({
	onCommit,
	disabled = false,
}: UseSwipeToDoneOptions): UseSwipeToDoneResult => {
	const [dragX, setDragX] = useState(0);
	const [isDragging, setIsDragging] = useState(false);

	const startXRef = useRef(0);
	const startYRef = useRef(0);
	const activeRef = useRef(false);
	const movedRef = useRef(false);

	const reset = useCallback(() => {
		activeRef.current = false;
		setIsDragging(false);
		setDragX(0);
	}, []);

	const onPointerDown = useCallback(
		(e: ReactPointerEvent) => {
			if (disabled) {
				return;
			}

			// Ignore non-primary mouse buttons; touch/pen have no button distinction.
			if (e.pointerType === "mouse" && e.button !== 0) {
				return;
			}

			activeRef.current = true;
			movedRef.current = false;
			startXRef.current = e.clientX;
			startYRef.current = e.clientY;
		},
		[disabled]
	);

	const onPointerMove = useCallback(
		(e: ReactPointerEvent) => {
			if (disabled || !activeRef.current) {
				return;
			}

			const dx = e.clientX - startXRef.current;
			const dy = e.clientY - startYRef.current;

			// Only engage once the gesture is clearly a horizontal-left drag — vertical scrolls and
			// tiny jitters must pass through untouched.
			if (!isDragging) {
				if (Math.abs(dx) < MOVE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) {
					return;
				}

				setIsDragging(true);
				movedRef.current = true;
				e.currentTarget.setPointerCapture?.(e.pointerId);
			}

			// Clamp to a leftward-only range.
			setDragX(Math.max(Math.min(dx, 0), -MAX_DRAG));
		},
		[disabled, isDragging]
	);

	const onPointerUp = useCallback(
		(e: ReactPointerEvent) => {
			if (!activeRef.current) {
				return;
			}

			const committed = e.clientX - startXRef.current <= -COMMIT_THRESHOLD;

			reset();

			if (committed) {
				onCommit();
			}
		},
		[onCommit, reset]
	);

	const shouldIgnoreClick = useCallback(() => {
		if (movedRef.current) {
			movedRef.current = false;

			return true;
		}

		return false;
	}, []);

	return {
		dragX,
		isDragging,
		bind: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: reset },
		shouldIgnoreClick,
	};
};
